// src/components/admin/AdminPage.js
import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  ClipboardList,
  Settings,
  Shield,
  TrendingUp,
  Search
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import AdminBookings from './AdminBookings';
import AdminCalendar from './AdminCalendar';
import AdminUsers from './AdminUsers';
import AdminDashboard from './AdminDashboard';
import StripeAudit from './StripeAudit';

const AdminPage = ({ isMobile }) => {
  const { user, userProfile, loading, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  // Redirect if not admin
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3A98B9] mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">You don't have permission to access the admin panel.</p>
          <button
            onClick={() => window.history.back()}
            className="bg-[#3A98B9] text-white px-6 py-2 rounded-lg hover:bg-[#2A87A8]"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
    { id: 'bookings', label: 'All Bookings', icon: ClipboardList },
    { id: 'calendar', label: 'Calendar View', icon: Calendar },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'stripe-audit', label: 'Stripe Audit', icon: Search },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <AdminDashboard isMobile={isMobile} />;
      case 'bookings':
        return <AdminBookings isMobile={isMobile} />;
      case 'calendar':
        return <AdminCalendar isMobile={isMobile} />;
      case 'users':
        return <AdminUsers isMobile={isMobile} />;
      case 'stripe-audit':
        return <StripeAudit isMobile={isMobile} />;
      default:
        return <AdminDashboard isMobile={isMobile} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
              <p className="text-sm text-gray-600">
                Welcome back, {userProfile?.displayName || userProfile?.firstName || user.email}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Shield className="h-3 w-3 mr-1" />
                Admin
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-[#3A98B9] text-white font-medium">
                <Settings className="h-5 w-5 inline-block mr-2" />
                Admin Tools
              </div>
              <div className="divide-y divide-gray-200">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                        activeTab === tab.id
                          ? 'bg-[#3A98B9]/10 text-[#3A98B9] border-r-2 border-[#3A98B9]'
                          : 'text-gray-700'
                      }`}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm min-h-[600px]">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;